import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { DemoSlot, DemoShiftControlFilterParams } from '@ts-demo-slot/interfaces'
import type { DemoShiftControlStore } from './interfaces'

import { useProfileStore } from '@stores/profile'
import { useCommonStore } from '@stores/common'

import { isFullArray } from '@helpers/array'

const getSlotsPaginatedData = (state: DemoShiftControlStore): DemoShiftControlStore['slotsPaginatedData'] => state.slotsPaginatedData || null
const getMeta = (state: DemoShiftControlStore): Meta | null => getSlotsPaginatedData(state)?.meta || null
const getSlots = (state: DemoShiftControlStore): DemoSlot[] => state.slotsPaginatedData?.slots || []

const getFilterFrom = (state: DemoShiftControlStore): string | null => {
  const profileStore = useProfileStore()
  return state.filterFrom || profileStore.getCurrentTimeSlotFrom
}
const getFilterTo = (state: DemoShiftControlStore): string | null => {
  const profileStore = useProfileStore()
  return state.filterTo || profileStore.getCurrentTimeSlotTo
}
const getFilterReservationType = (state: DemoShiftControlStore): DemoShiftControlStore['filterReservationType'] => state.filterReservationType
const getFilterSlotStatus = (state: DemoShiftControlStore): DemoShiftControlStore['filterSlotStatus'] => state.filterSlotStatus
const getFilterSortBy = (state: DemoShiftControlStore): DemoShiftControlStore['filterSortBy'] => state.filterSortBy

export const getters = {
  isLoading: (state: DemoShiftControlStore): DemoShiftControlStore['loading'] => state.loading,
  isShiftControlBusy: (state: DemoShiftControlStore): boolean => {
    const commonStore = useCommonStore()
    return state.loading || commonStore.isLoading
  },
  getMetrics: (state: DemoShiftControlStore): DemoShiftControlStore['metrics'] => state.metrics,
  isMetrics: (state: DemoShiftControlStore): boolean => !!state.metrics,
  getCustomersWaiting: (state: DemoShiftControlStore): number => state.metrics?.customers_waiting || 0,
  getLessonsInProgress: (state: DemoShiftControlStore): number => state.metrics?.lessons_in_progress || 0,
  getTutorsWaiting: (state: DemoShiftControlStore): number => state.metrics?.tutors_waiting || 0,
  getFilterReservationType: (state: DemoShiftControlStore): DemoShiftControlStore['filterReservationType'] => getFilterReservationType(state),
  getFilterSlotStatus: (state: DemoShiftControlStore): DemoShiftControlStore['filterSlotStatus'] => getFilterSlotStatus(state),
  getFilterSortBy: (state: DemoShiftControlStore): DemoShiftControlStore['filterSortBy'] => getFilterSortBy(state),
  getFilterFrom: (state: DemoShiftControlStore): DemoShiftControlStore['filterFrom'] => getFilterFrom(state),
  getFilterTo: (state: DemoShiftControlStore): DemoShiftControlStore['filterTo'] => getFilterTo(state),
  getFilterParams: (state: DemoShiftControlStore): DemoShiftControlFilterParams => {
    const status = getFilterSlotStatus(state)
    const reservationType = getFilterReservationType(state)
    return {
      from: getFilterFrom(state),
      to: getFilterTo(state),
      statuses: !status ? null : [status],
      reservation_types: !reservationType ? null : [reservationType],
      sort_by: getFilterSortBy(state),
    }
  },
  getSlotsPaginatedData: (state: DemoShiftControlStore): DemoShiftControlStore['slotsPaginatedData'] => getSlotsPaginatedData(state),
  getSlots: (state: DemoShiftControlStore): DemoSlot[] => getSlots(state),
  hasSlots: (state: DemoShiftControlStore): boolean => isFullArray(getSlots(state)),
  getMeta: (state: DemoShiftControlStore): Meta | null => getMeta(state),
  getCurrentPage: (state: DemoShiftControlStore): number => getMeta(state)?.current_page || 1,
  getLastPage: (state: DemoShiftControlStore): number => getMeta(state)?.last_page || 1,
  getPerPage: (state: DemoShiftControlStore): number => getMeta(state)?.per_page || 25,
  getWaitingBookings: (state: DemoShiftControlStore): DemoShiftControlStore['waitingBookings'] => state.waitingBookings,
}
